import { useState, useRef } from 'react';

interface UploadedFile {
  id: number;
  name: string;
  size: string;
  date: string;
  count: number;
  status: 'processing' | 'done' | 'error';
}

const FORMAT_RULES = [
  { icon: 'ri-file-text-line', color: 'text-primary-300', bg: 'bg-primary-300/10', title: 'TXT файл', desc: '1 строка = 1 номер телефона. Без заголовков.' },
  { icon: 'ri-file-excel-2-line', color: 'text-primary-300', bg: 'bg-primary-300/10', title: 'Excel (XLSX / CSV)', desc: 'Номера в первом столбце (A). Без объединённых ячеек. Первая строка — заголовок (пропускается).' },
  { icon: 'ri-phone-line', color: 'text-accent-200', bg: 'bg-accent-200/10', title: 'Формат номеров', desc: '+7XXXXXXXXXX или 8XXXXXXXXXX — 11 цифр. Без пробелов, скобок и тире.' },
  { icon: 'ri-close-circle-line', color: 'text-red-400', bg: 'bg-red-400/10', title: 'Не допускается', desc: 'Дубли, пустые строки между номерами, несколько номеров в одной строке.' },
];

const MOCK_UPLOADS: UploadedFile[] = [];

export default function HRResponseUpload() {
  const [uploads, setUploads] = useState<UploadedFile[]>(MOCK_UPLOADS);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploadError(null);

    Array.from(files).forEach((file) => {
      const allowed = ['text/plain', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'text/csv', 'application/csv'];
      const extOk = /\.(txt|xlsx|csv)$/i.test(file.name);

      if (!extOk && !allowed.includes(file.type)) {
        setUploadError(`Неподдерживаемый формат: ${file.name}. Разрешены: .txt, .xlsx, .csv`);
        return;
      }

      const newFile: UploadedFile = {
        id: Date.now() + Math.random(),
        name: file.name,
        size: formatSize(file.size),
        date: new Date().toLocaleDateString('ru-RU'),
        count: 0,
        status: 'processing',
      };
      setUploads((prev) => [newFile, ...prev]);

      setTimeout(() => {
        const estimated = Math.floor(Math.random() * 150) + 50;
        setUploads((prev) =>
          prev.map((f) =>
            f.id === newFile.id ? { ...f, count: estimated, status: 'done' } : f
          )
        );
      }, 1800);
    });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleRemove = (id: number) => {
    setUploads((prev) => prev.filter((f) => f.id !== id));
  };

  const STATUS_MAP = {
    processing: { label: 'Обработка...', color: 'text-accent-200', bg: 'bg-accent-300/10 border-accent-300/20', icon: 'ri-loader-4-line animate-spin' },
    done:       { label: 'Принято',       color: 'text-primary-300', bg: 'bg-primary-500/10 border-primary-500/20', icon: 'ri-check-line' },
    error:      { label: 'Ошибка',        color: 'text-red-400',     bg: 'bg-red-500/10 border-red-500/20',         icon: 'ri-close-line' },
  };

  return (
    <div className="max-w-3xl space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-1">Загрузка откликов</h2>
        <p className="text-foreground-500 text-sm">Загружайте файлы с номерами телефонов ежедневно</p>
      </div>

      {/* Instruction block */}
      <div className="bg-[#0e0e11] border border-[#18181c] rounded-2xl p-6">
        <div className="flex items-center gap-2.5 mb-5">
          <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-accent-300/10">
            <i className="ri-book-open-line text-accent-200 text-base" />
          </span>
          <h3 className="text-sm font-semibold text-white">Инструкция по формату файлов</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FORMAT_RULES.map((rule) => (
            <div key={rule.title} className="flex items-start gap-3 bg-[#000000] rounded-xl p-4">
              <span className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${rule.bg}`}>
                <i className={`${rule.icon} text-base ${rule.color}`} />
              </span>
              <div>
                <p className="text-xs font-semibold text-foreground-700 mb-0.5">{rule.title}</p>
                <p className="text-xs text-foreground-400 leading-relaxed">{rule.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 flex items-start gap-2.5 bg-accent-300/5 border border-accent-300/15 rounded-xl px-4 py-3">
          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-accent-200 text-sm" />
          </span>
          <p className="text-xs text-accent-200/80 leading-relaxed">
            Скачайте отклики с Авито или Зарплата.ру в личном кабинете, сохраните в формате .txt или .xlsx
            и загрузите сюда. <strong className="text-accent-200">Каждый отклик — 5 ₽.</strong> Файлы обрабатываются автоматически.
          </p>
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`relative rounded-2xl border-2 border-dashed transition-all duration-200 cursor-pointer flex flex-col items-center justify-center py-12 px-6 text-center
          ${isDragging
            ? 'border-accent-200/60 bg-accent-300/5'
            : 'border-[#26262c] bg-[#0e0e11] hover:border-accent-300/40 hover:bg-accent-300/5'
          }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".txt,.xlsx,.csv"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
        <div className={`w-16 h-16 flex items-center justify-center rounded-2xl mb-4 transition-colors ${isDragging ? 'bg-accent-300/20' : 'bg-[#141417]'}`}>
          <i className={`ri-upload-cloud-2-line text-3xl transition-colors ${isDragging ? 'text-accent-200' : 'text-foreground-400'}`} />
        </div>
        <p className="text-sm font-semibold text-foreground-700 mb-1">
          {isDragging ? 'Отпустите файл здесь' : 'Перетащите файл или нажмите для выбора'}
        </p>
        <p className="text-xs text-foreground-400">
          Поддерживаются: .txt, .xlsx, .csv — без ограничения по количеству файлов
        </p>
      </div>

      {uploadError && (
        <div className="flex items-center gap-2.5 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0">
            <i className="ri-error-warning-line text-red-400 text-sm" />
          </span>
          <p className="text-xs text-red-400">{uploadError}</p>
        </div>
      )}

      {/* Uploaded files list */}
      {uploads.length > 0 && (
        <div className="bg-[#0e0e11] border border-[#18181c] rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-[#18181c] flex items-center justify-between">
            <h3 className="text-sm font-semibold text-white">История загрузок</h3>
            <span className="text-xs text-foreground-400">{uploads.length} файлов</span>
          </div>
          <div className="divide-y divide-[#18181c]">
            {uploads.map((file) => {
              const st = STATUS_MAP[file.status];
              return (
                <div key={file.id} className="flex items-center gap-4 px-6 py-4">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#141417] flex-shrink-0">
                    <i className={`${/\.xlsx|csv/.test(file.name) ? 'ri-file-excel-2-line text-primary-300' : 'ri-file-text-line text-foreground-600'} text-base`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground-700 font-medium truncate">{file.name}</p>
                    <p className="text-xs text-foreground-400 mt-0.5">{file.size} · {file.date}</p>
                  </div>
                  {file.status === 'done' && (
                    <div className="text-right mr-2">
                      <p className="text-xs text-foreground-400">Откликов</p>
                      <p className="text-sm font-bold text-primary-300">{file.count}</p>
                    </div>
                  )}
                  <span className={`text-xs border rounded-full px-2.5 py-1 flex items-center gap-1.5 ${st.color} ${st.bg}`}>
                    <i className={`${st.icon} text-xs`} />
                    {st.label}
                  </span>
                  <button
                    onClick={() => handleRemove(file.id)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-300 hover:text-foreground-600 hover:bg-[#141417] transition-colors cursor-pointer flex-shrink-0"
                  >
                    <i className="ri-delete-bin-line text-sm" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {uploads.length === 0 && (
        <div className="flex flex-col items-center py-8 text-center">
          <p className="text-xs text-foreground-300">Загруженных файлов пока нет — ждём первый отклик!</p>
        </div>
      )}
    </div>
  );
}
